/**
 * Life Blueprint V2.9.9 — Migration Batch 05 (Sheet27: B58 / B59 / B60 / B61 / B62 / B63)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * STRICT SCOPE
 *   Implements ONLY Sheet27!B58, B59, B60, B61, B62, B63. No other Sheet27
 *   cells. Not wired into the main V2.9.9 pipeline (still throws).
 *
 * GOLDEN MASTER PROVENANCE (audited)
 *
 *   Sheet27!B58:
 *     =IF(OR($B$8>=23,$B$8<1),1,
 *      IF($B$8<3,2,
 *      IF($B$8<5,3,
 *      IF($B$8<7,4,
 *      IF($B$8<9,5,
 *      IF($B$8<11,6,
 *      IF($B$8<13,7,
 *      IF($B$8<15,8,
 *      IF($B$8<17,9,
 *      IF($B$8<19,10,
 *      IF($B$8<21,11,12)))))))))))
 *     Audited sample: B8=16 → B58=9
 *
 *   Sheet27!B59 = MOD(MOD(B53-1,5)*2, 10) + 1
 *     Audited sample: B53=8 → B59=5
 *
 *   Sheet27!B60 = MOD(B59-1 + B58-1, 10) + 1
 *     Audited sample: B59=5, B58=9 → B60=3
 *
 *   Sheet27!B61 = IF('01_个人资料'!$C$32 = "手动覆盖",
 *                    IF('01_个人资料'!$I$21<>"", LEFT('01_个人资料'!$I$21,1), ""),
 *                    VLOOKUP(B60, Sheet40!$A:$D, 2, FALSE))
 *
 *   Sheet27!B62 = IF('01_个人资料'!$C$32 = "手动覆盖",
 *                    IF('01_个人资料'!$I$21<>"", RIGHT('01_个人资料'!$I$21,1), ""),
 *                    VLOOKUP(B58, Sheet41!$A:$E, 2, FALSE))
 *
 *   Sheet27!B63 = B61 & B62
 *
 *   Audited automatic branch (this sample): C32 = "自动排盘" (audited), so
 *   C32 === MANUAL_MODE_TOKEN → FALSE and the VLOOKUPs fire:
 *     B60=3 → Sheet40!A4:B4 = 丙 (U+4E19, code point 19993)  → B61 = 丙
 *     B58=9 → Sheet41!A10:B10 = 申 (U+7533, code point 30003) → B62 = 申
 *     B63 = 丙申
 *
 *   Audited manual-branch evidence for this profile:
 *     I21 = "丙申" (LEN=2, AscW 19993 30003).
 *     Not consulted in the current GM because the outer manual guard is FALSE.
 *     Manual mechanics are exercised only by IMPLEMENTATION_MECHANIC_TEST
 *     cases.
 *
 * BEHAVIOURAL CONTRACT
 *   • The manual/automatic branch decision reuses Batch 03's audited
 *     `MANUAL_MODE_TOKEN` — no new token invention.
 *   • Automatic VLOOKUPs are exact-match. Missing keys throw the same
 *     `MISSING_REFERENCE_EVIDENCE` error established by Batch 03
 *     (Batch 03's Sheet40/Sheet41 partial reference is reused directly; the
 *     Excel formulas for B61/B62 do NOT wrap the VLOOKUP in IFERROR, so the
 *     Batch 03 contract is the correct semantics for these cells too).
 *   • B58 preserves the exact nested-IF ordering and comparison operators
 *     (`>=`, `<`) so boundary behavior matches Excel.
 *
 * DETERMINISM
 *   No randomness. Given identical input, byte-wise identical output.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { excelMod } from "@/engine/v299/batch01_sheet27";
import {
  MANUAL_MODE_TOKEN,
  PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE,
} from "@/engine/v299/batch03_sheet27";

/**
 * Sheet27!B58 — nested-IF chain on B8 with exact Excel comparison semantics.
 * Preserves ordering: OR(B8>=23, B8<1) tested first (top branch), then the
 * strict-less-than window branches from B8<3 through B8<21, with a final
 * else of 12. `>=` and `<` semantics are exact; do not swap operators.
 *
 * @param {number} upstreamValue_B8   integer hour-of-day / B8 (0..24 domain)
 * @returns {number} integer in [1, 12]
 */
export function computeB58(upstreamValue_B8) {
  if (!Number.isInteger(upstreamValue_B8)) {
    throw new TypeError("[Batch05·B58] upstreamValue_B8 must be an integer");
  }
  const B8 = upstreamValue_B8;
  if (B8 >= 23 || B8 < 1) return 1;
  if (B8 < 3) return 2;
  if (B8 < 5) return 3;
  if (B8 < 7) return 4;
  if (B8 < 9) return 5;
  if (B8 < 11) return 6;
  if (B8 < 13) return 7;
  if (B8 < 15) return 8;
  if (B8 < 17) return 9;
  if (B8 < 19) return 10;
  if (B8 < 21) return 11;
  return 12; // Excel else-branch
}

/**
 * Sheet27!B59 = MOD(MOD(B53-1, 5) * 2, 10) + 1
 * Reuses Batch 01's Excel-compatible MOD helper.
 * @param {number} upstreamValue_B53
 * @returns {number}
 */
export function computeB59(upstreamValue_B53) {
  if (!Number.isInteger(upstreamValue_B53)) {
    throw new TypeError("[Batch05·B59] upstreamValue_B53 must be an integer");
  }
  return excelMod(excelMod(upstreamValue_B53 - 1, 5) * 2, 10) + 1;
}

/**
 * Sheet27!B60 = MOD(B59-1 + B58-1, 10) + 1
 * @param {number} upstreamValue_B59
 * @param {number} upstreamValue_B58
 * @returns {number}
 */
export function computeB60(upstreamValue_B59, upstreamValue_B58) {
  if (!Number.isInteger(upstreamValue_B59) || !Number.isInteger(upstreamValue_B58)) {
    throw new TypeError(
      "[Batch05·B60] upstreamValue_B59 and upstreamValue_B58 must be integers"
    );
  }
  return excelMod(upstreamValue_B59 - 1 + upstreamValue_B58 - 1, 10) + 1;
}

function isNonEmptyString(v) {
  return typeof v === "string" && v.length > 0;
}

/**
 * Excel-compatible exact-match VLOOKUP. Missing keys throw the Batch 03
 * `MISSING_REFERENCE_EVIDENCE` error contract — never a guessed value.
 */
function vlookupExactMatch(key, refTable) {
  if (!refTable || !Array.isArray(refTable.rows)) {
    throw new TypeError("[Batch05·VLOOKUP] reference table missing or malformed");
  }
  const row = refTable.rows.find((r) => r.a === key);
  if (!row) {
    const err = new Error(
      `[Batch05·VLOOKUP] Missing ${refTable.codeName} key ${JSON.stringify(key)}. ` +
        `Only PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE has been migrated for ` +
        `this reference table; the required row has not yet been audited. ` +
        `No result guessed.`
    );
    err.code = "MISSING_REFERENCE_EVIDENCE";
    err.refTable = refTable.codeName;
    err.key = key;
    throw err;
  }
  return row.b;
}

/**
 * Sheet27!B61.
 * Manual/automatic branch derived from the actual audited C32 comparison.
 *
 * @param {{
 *   upstreamC32: string,
 *   upstreamValue_B60?: number,
 *   upstreamI21?: string,
 *   reference?: typeof PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE
 * }} slice
 * @returns {string}
 */
export function computeB61(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch05·B61] slice must be an object");
  }
  const ref = slice.reference || PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE;
  if (slice.upstreamC32 === MANUAL_MODE_TOKEN) {
    if (isNonEmptyString(slice.upstreamI21)) {
      return slice.upstreamI21.substring(0, 1); // LEFT(I21, 1)
    }
    return "";
  }
  if (!Number.isInteger(slice.upstreamValue_B60)) {
    throw new TypeError(
      "[Batch05·B61] upstreamValue_B60 must be an integer in the automatic branch"
    );
  }
  return vlookupExactMatch(slice.upstreamValue_B60, ref.Sheet40);
}

/**
 * Sheet27!B62.
 * @param {{
 *   upstreamC32: string,
 *   upstreamValue_B58?: number,
 *   upstreamI21?: string,
 *   reference?: typeof PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE
 * }} slice
 * @returns {string}
 */
export function computeB62(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch05·B62] slice must be an object");
  }
  const ref = slice.reference || PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE;
  if (slice.upstreamC32 === MANUAL_MODE_TOKEN) {
    if (isNonEmptyString(slice.upstreamI21)) {
      return slice.upstreamI21.substring(slice.upstreamI21.length - 1); // RIGHT(I21,1)
    }
    return "";
  }
  if (!Number.isInteger(slice.upstreamValue_B58)) {
    throw new TypeError(
      "[Batch05·B62] upstreamValue_B58 must be an integer in the automatic branch"
    );
  }
  return vlookupExactMatch(slice.upstreamValue_B58, ref.Sheet41);
}

/**
 * Sheet27!B63 = B61 & B62 (deterministic Excel string concatenation).
 */
export function computeB63(B61, B62) {
  return String(B61) + String(B62);
}

/**
 * Aggregate entry point for Batch 05.
 *
 * @param {{
 *   upstreamC32: string,
 *   upstreamValue_B8: number,
 *   upstreamValue_B53: number,
 *   upstreamI21?: string,
 *   reference?: typeof PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE
 * }} slice
 * @returns {{B58:number,B59:number,B60:number,B61:string,B62:string,B63:string,_provenance:Object}}
 */
export function computeBatch05Sheet27(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch05] slice must be an object");
  }
  const B58 = computeB58(slice.upstreamValue_B8);
  const B59 = computeB59(slice.upstreamValue_B53);
  const B60 = computeB60(B59, B58);
  const sliceExt = { ...slice, upstreamValue_B58: B58, upstreamValue_B60: B60 };
  const B61 = computeB61(sliceExt);
  const B62 = computeB62(sliceExt);
  const B63 = computeB63(B61, B62);
  return {
    B58,
    B59,
    B60,
    B61,
    B62,
    B63,
    _provenance: {
      excelSources: [
        "Sheet27!B58",
        "Sheet27!B59",
        "Sheet27!B60",
        "Sheet27!B61",
        "Sheet27!B62",
        "Sheet27!B63",
      ],
      formulas: {
        "Sheet27!B58":
          "=IF(OR($B$8>=23,$B$8<1),1,IF($B$8<3,2,IF($B$8<5,3,IF($B$8<7,4," +
          "IF($B$8<9,5,IF($B$8<11,6,IF($B$8<13,7,IF($B$8<15,8," +
          "IF($B$8<17,9,IF($B$8<19,10,IF($B$8<21,11,12)))))))))))",
        "Sheet27!B59": "=MOD(MOD(B53-1,5)*2,10)+1",
        "Sheet27!B60": "=MOD(B59-1+B58-1,10)+1",
        "Sheet27!B61":
          "=IF('01_个人资料'!$C$32=\"手动覆盖\"," +
          "IF('01_个人资料'!$I$21<>\"\",LEFT('01_个人资料'!$I$21,1),\"\")," +
          "VLOOKUP(B60,Sheet40!$A:$D,2,FALSE))",
        "Sheet27!B62":
          "=IF('01_个人资料'!$C$32=\"手动覆盖\"," +
          "IF('01_个人资料'!$I$21<>\"\",RIGHT('01_个人资料'!$I$21,1),\"\")," +
          "VLOOKUP(B58,Sheet41!$A:$E,2,FALSE))",
        "Sheet27!B63": "=B61&B62",
      },
      lookupSources: {
        B61: { worksheetIndex: 40, codeName: "Sheet40" },
        B62: { worksheetIndex: 41, codeName: "Sheet41" },
      },
      auditedRecords: {
        "Sheet40!A4:B4":   { key: 3, value: "\u4E19", unicodeCodePoint: 19993, auditedInBatch: "Batch05" },
        "Sheet41!A10:B10": { key: 9, value: "\u7533", unicodeCodePoint: 30003, auditedInBatch: "Batch05" },
      },
      partialReferenceEvidence: {
        Sheet40: PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE.Sheet40.status,
        Sheet41: PARTIAL_GOLDEN_MASTER_REFERENCE_EVIDENCE.Sheet41.status,
      },
      manualModeToken: MANUAL_MODE_TOKEN,
      note:
        "Business meaning of B58/B59/B60/B61/B62/B63 and of Sheet40/Sheet41 " +
        "lookup values is intentionally NOT labelled — no independent evidence " +
        "has established their semantics.",
    },
  };
}
